// Dynamic Analytics Loader - Advanced Obfuscated Implementation
import { analyticsConfig, buildUrls } from '../config/analytics.config';

class AnalyticsManager {
  constructor() {
    this.initialized = false;
    this.config = null;
    this.urls = null;
    this.loadDelay = 1500;
    this.retryCount = 3;
    
    // Initialize configuration on construction
    this.initConfig();
  }

  // Initialize obfuscated configuration
  initConfig() {
    try {
      this.config = analyticsConfig.getConfig();
      this.urls = buildUrls(this.config);
    } catch (error) {
      console.warn('Config initialization failed:', error);
    }
  }

  // Advanced script injection with retry logic
  async injectScript(url, attributes = {}, retries = this.retryCount) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      
      // Apply attributes
      Object.entries(attributes).forEach(([key, value]) => {
        if (key === 'innerHTML') {
          script.innerHTML = value;
        } else {
          script.setAttribute(key, value);
        }
      });

      // Set source
      script.src = url;

      // Handle success/failure with retry logic
      script.onload = () => resolve(true);
      script.onerror = async () => {
        if (retries > 0) {
          console.warn(`Script failed, retrying... (${retries} attempts left)`);
          document.head.removeChild(script);
          setTimeout(async () => {
            try {
              await this.injectScript(url, attributes, retries - 1);
              resolve(true);
            } catch (e) {
              reject(e);
            }
          }, 1000);
        } else {
          reject(new Error(`Failed to load script: ${url}`));
        }
      };

      // Inject into DOM
      document.head.appendChild(script);
    });
  }

  // Load Google Analytics with obfuscated configuration
  // async loadGA() {
  //   if (!this.config?.gaId || !this.urls?.ga) return false;

  //   try {
  //     // Load gtag script
  //     await this.injectScript(this.urls.ga, { async: 'true' });

  //     // Initialize dataLayer and gtag function
  //     window.dataLayer = window.dataLayer || [];
  //     const gtag = (...args) => window.dataLayer.push(args);
  //     window.gtag = gtag;

  //     // Configure GA4
  //     gtag('js', new Date());
  //     gtag('config', this.config.gaId, {
  //       // Enhanced configuration
  //       anonymize_ip: true,
  //       allow_google_signals: false,
  //       cookie_flags: 'max-age=7200;secure;samesite=strict'
  //     });

  //     return true;
  //   } catch (error) {
  //     console.warn('GA4 loading failed:', error);
  //     return false;
  //   }
  // }

  // Load Google Tag Manager with obfuscated configuration  
  async loadGTM() {
    if (!this.config?.gtmId || !this.urls?.gtm) return false;

    try {
      // Initialize dataLayer before GTM script loads
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js'
      });

      // Create GTM script with proper attributes
      const gtmScript = document.createElement('script');
      gtmScript.async = true;
      gtmScript.src = this.urls.gtm;
      
      // Add GTM script to head
      document.head.appendChild(gtmScript);

      // Add noscript fallback to body
      this.addGTMNoscript();

      console.log('✅ GTM: Script loaded successfully');
      console.log('🔍 GTM ID:', this.config.gtmId);
      console.log('🔍 GTM URL:', this.urls.gtm);

      return true;
    } catch (error) {
      console.warn('GTM loading failed:', error);
      return false;
    }
  }

  // Add Google Search Console verification meta tag dynamically
  addGSCVerification() {
    if (!this.config?.gscVerification) return false;

    try {
      // Check if verification meta tag already exists
      const existingMeta = document.querySelector('meta[name="google-site-verification"]');
      if (existingMeta) {
        // Update existing meta tag with our verification code
        existingMeta.setAttribute('content', this.config.gscVerification);
        console.log('✅ GSC Verification: Updated existing meta tag');
      } else {
        // Create new meta tag
        const meta = document.createElement('meta');
        meta.setAttribute('name', 'google-site-verification');
        meta.setAttribute('content', this.config.gscVerification);
        document.head.appendChild(meta);
        console.log('✅ GSC Verification: Created new meta tag');
      }

      // Debug: Log the current verification content
      const finalMeta = document.querySelector('meta[name="google-site-verification"]');
      console.log('🔍 GSC Verification Content:', finalMeta?.getAttribute('content'));

      return true;
    } catch (error) {
      console.warn('GSC verification setup failed:', error);
      return false;
    }
  }

  // Add GTM noscript fallback
  addGTMNoscript() {
    if (!this.urls?.gtmNoscript) return;

    try {
      // Check if noscript already exists
      const existingNoscript = document.querySelector('noscript[data-gtm="true"]');
      if (existingNoscript) {
        console.log('✅ GTM: Noscript already exists');
        return;
      }

      const noscript = document.createElement('noscript');
      noscript.setAttribute('data-gtm', 'true');
      
      const iframe = document.createElement('iframe');
      iframe.src = this.urls.gtmNoscript;
      iframe.height = '0';
      iframe.width = '0';
      iframe.style.cssText = 'display:none;visibility:hidden';
      
      noscript.appendChild(iframe);
      document.body.insertBefore(noscript, document.body.firstChild);
      
      console.log('✅ GTM: Noscript added to body');
    } catch (error) {
      console.warn('GTM noscript setup failed:', error);
    }
  }

  // Load Cloudflare Insights with obfuscated token
  async loadCloudflare() {
    if (!this.config?.cfToken || !this.urls?.cf) return false;

    try {
      const beaconConfig = {
        version: "2024.11.0",
        token: this.config.cfToken,
        r: 1,
        server_timing: {
          name: {
            cfCacheStatus: true,
            cfEdge: true,
            cfExtPri: true,
            cfL4: true,
            cfOrigin: true,
            cfSpeedBrain: true
          }
        }
      };

      await this.injectScript(this.urls.cf, {
        defer: 'true',
        'data-cf-beacon': JSON.stringify(beaconConfig),
        integrity: 'sha512-ZpsOmlRQV6y907TI0dKBHq9Md29nnaEIPlkf84rnaERnq6zvWvPUqr2ft8M1aS28oN72PdrCzSjY4U6VaAw1EQ==',
        crossorigin: 'anonymous'
      });

      return true;
    } catch (error) {
      console.warn('Cloudflare Insights loading failed:', error);
      return false;
    }
  }

  // Smart initialization with performance optimization
  async init() {
    if (this.initialized || typeof window === 'undefined') return;

    // Wait for DOM ready
    if (document.readyState === 'loading') {
      await new Promise(resolve => {
        document.addEventListener('DOMContentLoaded', resolve, { once: true });
      });
    }

    // Delayed loading for better performance
    setTimeout(async () => {
      try {
        // Add Google Search Console verification first
        this.addGSCVerification();

        // Load analytics services in parallel (GA4 removed)
        const loadResults = await Promise.allSettled([
          this.loadGTM(),
          this.loadCloudflare()
        ]);

        // Process results
        const [gtm, cf] = loadResults.map(result => 
          result.status === 'fulfilled' ? result.value : false
        );

        // Mark as initialized if at least one service loaded
        this.initialized = gtm || cf;

        // Log status (can be removed in production)
        if (process.env.NODE_ENV === 'development') {
          console.log('📊 Analytics Services:', { 
            GTM: gtm ? '✅' : '❌', 
            CF: cf ? '✅' : '❌',
            GSC: this.config?.gscVerification ? '✅' : '❌'
          });
          
          // Additional GTM debug info
          if (gtm) {
            console.log('🔧 GTM Debug Info:', {
              'GTM ID': this.config.gtmId,
              'DataLayer': window.dataLayer ? `${window.dataLayer.length} events` : 'Not found',
              'GTM Script': document.querySelector(`script[src*="${this.config.gtmId}"]`) ? 'Loaded' : 'Not found',
              'GTM Noscript': document.querySelector('noscript[data-gtm="true"]') ? 'Added' : 'Not found'
            });
          }
        }

      } catch (error) {
        console.error('Analytics initialization failed:', error);
      }
    }, this.loadDelay);
  }

  // Enhanced event tracking with validation
  trackEvent(eventName, parameters = {}) {
    if (!this.initialized || !eventName) return false;

    try {
      // Sanitize event name
      const sanitizedEvent = eventName.replace(/[^a-zA-Z0-9_]/g, '_');

      // GA4 tracking
      if (window.gtag && this.config?.gaId) {
        window.gtag('event', sanitizedEvent, {
          ...parameters,
          transport_type: 'beacon' // Ensure delivery
        });
      }

      // GTM tracking
      if (window.dataLayer) {
        window.dataLayer.push({
          event: sanitizedEvent,
          timestamp: Date.now(),
          ...parameters
        });
      }

      return true;
    } catch (error) {
      console.warn('Event tracking failed:', error);
      return false;
    }
  }

  // Enhanced page view tracking
  trackPageView(page, title = document.title) {
    if (!this.initialized) return false;

    try {
      const pageData = {
        page_path: page || window.location.pathname,
        page_title: title,
        page_location: window.location.href
      };

      // GA4 page view
      if (window.gtag && this.config?.gaId) {
        window.gtag('config', this.config.gaId, pageData);
      }

      // GTM page view
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'page_view',
          ...pageData
        });
      }

      return true;
    } catch (error) {
      console.warn('Page view tracking failed:', error);
      return false;
    }
  }

  // Conversion tracking
  trackConversion(conversionId, value = null, currency = 'USD') {
    const conversionData = {
      send_to: conversionId,
      ...(value && { value: parseFloat(value), currency })
    };

    return this.trackEvent('conversion', conversionData);
  }

  // User engagement tracking
  trackEngagement(action, category = 'engagement', value = 1) {
    return this.trackEvent('engagement', {
      event_category: category,
      event_action: action,
      event_value: value,
      engagement_time_msec: Date.now()
    });
  }

  // Custom user properties
  setUserProperties(properties = {}) {
    if (!this.initialized) return false;

    try {
      if (window.gtag) {
        window.gtag('config', this.config?.gaId, {
          custom_map: properties
        });
      }

      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'user_properties_set',
          user_properties: properties
        });
      }

      return true;
    } catch (error) {
      console.warn('User properties setting failed:', error);
      return false;
    }
  }
}

// Export singleton instance
export const analytics = new AnalyticsManager();

// Convenience exports
export const initializeAnalytics = () => analytics.init();
export const trackEvent = (eventName, parameters) => analytics.trackEvent(eventName, parameters);
export const trackPageView = (page, title) => analytics.trackPageView(page, title);
export const trackConversion = (id, value, currency) => analytics.trackConversion(id, value, currency);
export const trackEngagement = (action, category, value) => analytics.trackEngagement(action, category, value);
export const setUserProperties = (properties) => analytics.setUserProperties(properties);

export default analytics;